import React from 'react';
import { useTranslation } from 'react-i18next';
import PaidMilestoneNotice from './PaidMilestoneNotice';
import { contractTitle } from '../utils/contractTitle';
import StageVariationNotice from './StageVariationNotice';
import type { Contract, ContractReplacement, Milestone } from '../types';

interface ContractReplacementNoticeProps {
  contract: Contract;
  originalContract?: Contract | null;
  settlement?: ContractReplacement | null;
  compact?: boolean;
  milestones?: Milestone[];
  onPay?: () => void;
  onSign?: () => void;
  isSigning?: boolean;
  onApproveProposal?: () => void;
  onRejectProposal?: () => void;
  onWithdrawProposal?: () => void;
  isUpdatingProposal?: boolean;
}

const formatOre = (amountOre: number) =>
  new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'SEK',
    maximumFractionDigits: 2
  }).format(amountOre / 100);

export default function ContractReplacementNotice({
  contract,
  originalContract,
  settlement,
  compact = false,
  milestones = [],
  onPay,
  onSign,
  isSigning = false,
  onApproveProposal,
  onRejectProposal,
  onWithdrawProposal,
  isUpdatingProposal = false
}: ContractReplacementNoticeProps) {
  const { t } = useTranslation();
  if (settlement?.amendmentScope) {
    return (
      <StageVariationNotice
        contract={contract}
        settlement={settlement}
        compact={compact}
        onPay={onPay}
        onSign={onSign}
        isSigning={isSigning}
      />
    );
  }
  const version = contract.contractVersion ?? 1;
  const status = contract.versionStatus ?? 'current';
  if (version <= 1 && !contract.supersededByContractId && !settlement) return null;
  const originalReference = originalContract
    ? t('contractReplacement.originalReference', {
        number: originalContract.contractNumber,
        version: originalContract.contractVersion ?? 1,
        description: contractTitle(originalContract)
      })
    : null;
  const isOriginalVersion = settlement?.originalContractId === contract.id;
  const isReplacementVersion = settlement?.replacementContractId === contract.id;
  const proposalVersion = settlement?.contract?.contractVersion ??
    (isReplacementVersion ? version : version + 1);
  const isBuyerProposal = settlement?.initiatorRole === 'person';
  const financialPreview = (() => {
    if (!settlement || settlement.status !== 'draft') return null;
    if (settlement.refundAmountOre > 0 && settlement.additionalPaymentOre > 0) {
      return t('contractReplacement.financialPreviewBoth', { retained: formatOre(settlement.retainedFundingOre), refund: formatOre(settlement.refundAmountOre), additional: formatOre(settlement.additionalPaymentOre) });
    }
    if (settlement.refundAmountOre > 0) {
      return t('contractReplacement.financialPreviewRefund', {
        retained: formatOre(settlement.retainedFundingOre),
        refund: formatOre(settlement.refundAmountOre)
      });
    }
    if (settlement.additionalPaymentOre > 0) {
      return t('contractReplacement.financialPreviewAdditional', {
        retained: formatOre(settlement.retainedFundingOre),
        additional: formatOre(settlement.additionalPaymentOre)
      });
    }
    return t('contractReplacement.financialPreviewNone');
  })();
  const financialPreviewText = financialPreview ? `${financialPreview} ` : '';

  const content = (() => {
    if (isBuyerProposal && settlement?.proposalStatus === 'pending') {
      return {
        variant: 'warning',
        icon: 'clock',
        title: t('contractReplacement.proposalPendingTitle', { version: proposalVersion }),
        text: t('contractReplacement.proposalPendingText', { financialPreview: financialPreviewText })
      };
    }
    if (isBuyerProposal && settlement?.proposalStatus === 'rejected') {
      return {
        variant: 'danger',
        icon: 'x-circle',
        title: t('contractReplacement.proposalRejectedTitle', { version: proposalVersion }),
        text: t('contractReplacement.proposalRemainsInHistoryText', {
          responseMessage: settlement.proposalResponseMessage ? `${settlement.proposalResponseMessage} ` : ''
        })
      };
    }
    if (isBuyerProposal && settlement?.proposalStatus === 'withdrawn') {
      return {
        variant: 'secondary',
        icon: 'rotate-ccw',
        title: t('contractReplacement.proposalWithdrawnTitle', { version: proposalVersion }),
        text: t('contractReplacement.proposalRemainsInHistoryText', {
          responseMessage: settlement.proposalResponseMessage ? `${settlement.proposalResponseMessage} ` : ''
        })
      };
    }
    if (status === 'superseded') {
      return {
        variant: 'secondary',
        icon: 'archive',
        title: t('contractReplacement.supersededTitle', { version }),
        text: t('contractReplacement.supersededText')
      };
    }
    if (status === 'activation_failed' || settlement?.status === 'failed') {
      return {
        variant: 'danger',
        icon: 'alert-triangle',
        title: t('contractReplacement.activationFailedTitle', { version }),
        text:
          settlement?.failureMessage ??
          t('contractReplacement.activationFailedText')
      };
    }
    if (isOriginalVersion && settlement?.status !== 'completed') {
      return {
        variant: 'info',
        icon: 'shield',
        title: t('contractReplacement.originalVersionActiveTitle', { version }),
        text: t('contractReplacement.originalVersionActiveText', { financialPreview: financialPreviewText })
      };
    }
    if (status === 'proposed') {
      if (contract.draftStatus === 'draft_rejected' || contract.signingStatus === 'declined') {
        return {
          variant: 'warning', icon: 'edit-3',
          title: t('counterproposal.rejectedTitle', { version }),
          text: t('counterproposal.rejectedText')
        };
      }
      if (contract.companySigned && !contract.personSigned) {
        return {
          variant: 'warning',
          icon: 'edit-3',
          title: t('contractReplacement.proposedAwaitingBuyerTitle', { version }),
          text: t('contractReplacement.proposedAwaitingSignatureText', { financialPreview: financialPreviewText })
        };
      }
      if (!contract.companySigned && contract.personSigned) {
        return {
          variant: 'warning',
          icon: 'edit-3',
          title: t('contractReplacement.proposedAwaitingCompanyTitle', { version }),
          text: t('contractReplacement.proposedAwaitingSignatureText', { financialPreview: financialPreviewText })
        };
      }
      if (contract.companySigned && contract.personSigned) {
        return {
          variant: 'warning',
          icon: 'clock',
          title: t('contractReplacement.proposedSignedTitle', { version }),
          text: t('contractReplacement.proposedSignedText')
        };
      }
      return {
        variant: 'info',
        icon: 'edit-3',
        title: t('contractReplacement.proposedDraftTitle', { version }),
        text: t('contractReplacement.proposedDraftText', { financialPreview: financialPreviewText })
      };
    }
    if (settlement?.status === 'awaiting_payment') {
      return {
        variant: 'warning',
        icon: 'credit-card',
        title: t('contractReplacement.proposedSignedTitle', { version }),
        text: t('contractReplacement.awaitingPaymentText', { amount: formatOre(settlement.additionalPaymentOre) })
      };
    }
    if (settlement?.status === 'refund_pending') {
      return {
        variant: 'warning',
        icon: 'refresh-cw',
        title: t('contractReplacement.refundPendingTitle', { amount: formatOre(settlement.refundAmountOre) }),
        text: t('contractReplacement.refundPendingText')
      };
    }
    if (status === 'settlement_pending') {
      return {
        variant: 'warning',
        icon: 'clock',
        title: t('contractReplacement.settlementPendingTitle', { version }),
        text: t('contractReplacement.settlementPendingText')
      };
    }
    return {
      variant: 'success',
      icon: 'check-circle',
      title: t('contractReplacement.resolvedTitle', { version }),
      text: t('contractReplacement.resolvedText')
    };
  })();
  const canSign =
    isReplacementVersion &&
    status === 'proposed' &&
    (!isBuyerProposal || settlement?.proposalStatus === 'approved') &&
    contract.companySigned &&
    !contract.personSigned &&
    !!contract.externalSigningProcessId &&
    !!onSign;
  const canRespondToProposal =
    isBuyerProposal &&
    settlement?.proposalStatus === 'pending' &&
    (!!onApproveProposal || !!onRejectProposal);
  const canWithdrawProposal =
    isBuyerProposal &&
    settlement?.proposalStatus === 'pending' &&
    !!onWithdrawProposal;
  const showActions =
    canSign ||
    canRespondToProposal ||
    canWithdrawProposal ||
    (!compact &&
      settlement?.status === 'awaiting_payment' &&
      !!onPay);

  return (
    <div
      className={`alert alert-${content.variant} ${compact ? 'py-2 px-3 mb-2' : ''}`}
      data-testid="contract-replacement-notice"
      role={canSign || canRespondToProposal || canWithdrawProposal ? 'alert' : 'status'}
    >
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div>
          <div className="fw-semibold">
            <i className={`feather-${content.icon} me-2`}></i>
            {content.title}
            {canSign && (
              <span className="badge bg-warning text-dark ms-2">
                {t('contractReplacement.actionRequired')}
              </span>
            )}
          </div>
          {isBuyerProposal && settlement?.reason && (
            <div className="small mt-2" style={{ whiteSpace: 'pre-wrap' }}>
              <strong>{t('contractReplacement.buyerProposalLabel')}</strong>{' '}
              {settlement.reason}
            </div>
          )}
          {originalReference && status !== 'superseded' && (
            <div className="small mt-1">
              <strong>{t('contractReplacement.replacesLabel')}</strong>{' '}
              {originalReference}
            </div>
          )}
          {(!compact ||
            (isReplacementVersion &&
              ['draft', 'completed'].includes(settlement?.status ?? ''))) && (
            <div className="small mt-1">{content.text}</div>
          )}
          {isReplacementVersion &&
            !compact &&
            milestones.map((milestone) => (
              <PaidMilestoneNotice key={milestone.id} milestone={milestone} />
            ))}
        </div>
        {showActions && (
          <div className="d-flex flex-wrap gap-2">
            {canSign && (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={onSign}
                disabled={isSigning}
              >
                <i className="feather-check-square me-1"></i>
                {isSigning ? t('contractReplacement.openingSigning') : t('contractReplacement.reviewAndSign')}
              </button>
            )}
            {canRespondToProposal && onApproveProposal && (
              <button
                type="button"
                className="btn btn-sm btn-success"
                onClick={onApproveProposal}
                disabled={isUpdatingProposal}
              >
                <i className="feather-check me-1"></i>
                {isUpdatingProposal ? t('contractReplacement.saving') : t('contractReplacement.approveProposal')}
              </button>
            )}
            {canRespondToProposal && onRejectProposal && (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={onRejectProposal}
                disabled={isUpdatingProposal}
              >
                <i className="feather-x me-1"></i>
                {t('contractReplacement.rejectProposal')}
              </button>
            )}
            {canWithdrawProposal && onWithdrawProposal && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={onWithdrawProposal}
                disabled={isUpdatingProposal}
              >
                <i className="feather-rotate-ccw me-1"></i>
                {isUpdatingProposal ? t('contractReplacement.withdrawing') : t('contractReplacement.withdrawProposal')}
              </button>
            )}
            {!compact && settlement?.status === 'awaiting_payment' && onPay && (
              <button type="button" className="btn btn-sm btn-warning" onClick={onPay}>
                {t('contractReplacement.payDifference')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
